import React, { useState } from 'react';
import logo from '../assets/logos/olympic-logo-white.svg';
import trustpilotLogo from '../assets/logos/trustpilot-white.svg';
import facebook from '../assets/icons/social-facebook.svg';
import instagram from '../assets/icons/social-instagram.svg';
import tiktok from '../assets/icons/social-tiktok.svg';
import youtube from '../assets/icons/social-youtube.svg';
import { TickIcon } from './icons';
import { useSiteChrome } from './useSiteChrome';

/**
 * The site footer for the apps (Titan, My Booking) - the same footer as the website (Olympic.Site
 * Views/Shared/Components/GlobalFooter), from the home page's Site Footer fields (window.siteChrome). On phones each
 * column folds away under its title.
 * The content is the page's window.siteChrome (written by the app's layout), or `chrome` when given.
 * `compact`: one slim band with no links - the accreditation logos and the legal text - for pages you never scroll to
 * the bottom of (Titan's infinite-scroll search results).
 */
const SPLIT_ABOVE = 8; // a longer column takes two grid columns, its links in two halves
// The designer's round social icons (white disc, blue glyph); networks without one fall back to their name
const SOCIAL_ICONS = { facebook, instagram, tiktok, youtube };

function SocialIcon({ network }) {
    const src = SOCIAL_ICONS[(network || '').toLowerCase()];
    return src ? <img src={src} alt="" className="size-12 md:size-10" /> : <span className="text-sm">{network}</span>;
}

function FoldingColumn({ title, className = '', children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`border-b border-white/20 md:border-0 ${className}`}>
            <button type="button" className="md:hidden flex w-full items-center justify-between py-3.5 text-lg font-medium" onClick={() => setOpen(!open)} aria-expanded={open}>
                {title}<span className="text-2xl leading-none">{open ? '−' : '+'}</span>
            </button>
            <p className="oly-footer-heading hidden md:block">{title}</p>
            <div className={`pb-4 md:pb-0 ${open ? '' : 'max-md:hidden'}`}>{children}</div>
        </div>
    );
}

function Links({ links }) {
    return links.map((link, i) => (
        <a key={i} className="oly-footer-link" href={link.url} target={link.target || undefined} rel={link.target === '_blank' ? 'noopener' : undefined}>{link.name}</a>
    ));
}

function Logo({ logo, imageClass }) {
    const image = <img src={logo.imageUrl} alt={logo.altText || 'Olympic Holidays'} className={imageClass} loading="lazy" />;
    return logo.url ? <a href={logo.url} target={logo.target || undefined}>{image}</a> : image;
}

export function SiteFooter({ chrome: given, version, compact = false }) {
    const chrome = useSiteChrome(given);
    const footer = chrome?.footer;
    if (!footer) {
        return <footer className="oly-footer min-h-40" role="contentinfo" />;
    }
    const columns = footer.columns || [];
    const promises = footer.promises || [];
    const hasPromise = !!footer.promiseTitle && (promises.length > 0 || !!footer.trustpilotLink);
    const width = columns.reduce((sum, c) => sum + (c.links.length > SPLIT_ABOVE ? 2 : 1), 0) + (hasPromise ? 1 : 0);
    const gridCols = width <= 2 ? 'lg:grid-cols-2' : width === 3 ? 'lg:grid-cols-3' : width === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-5';
    const accreditations = footer.accreditations || [];
    const awards = footer.awards || [];

    if (compact) {
        return (
            <footer className="oly-footer" role="contentinfo">
                <div className="oly-container flex flex-col items-center gap-4 py-6 md:flex-row md:justify-between">
                    {accreditations.length ? (
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            {accreditations.map((logo, i) => (
                                <img key={i} src={logo.imageUrl} alt={logo.altText || ''} className="h-7 w-auto rounded-sm" loading="lazy" />
                            ))}
                        </div>
                    ) : null}
                    {footer.legalText ? <p className="max-w-xl text-center text-xs text-white/70 md:text-right">{footer.legalText}</p> : null}
                </div>
            </footer>
        );
    }

    return (
        <footer className="oly-footer" role="contentinfo">
            <div className="oly-container py-12 lg:py-16">
                <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:justify-between">
                    <a href={footer.homeUrl || '/'} aria-label="Olympic Holidays home">
                        <img src={logo} alt="Olympic Holidays" className="h-12 w-auto" width="143" height="56" loading="lazy" />
                    </a>
                    {footer.socialLinks?.length ? (
                        <div className="flex gap-3">
                            {footer.socialLinks.map((social) => (
                                <a key={social.network} href={social.url} target="_blank" rel="noopener" aria-label={social.network} className="rounded-full hover:opacity-85">
                                    <SocialIcon network={social.network} />
                                </a>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className={`mt-8 md:mt-10 md:grid md:gap-10 sm:grid-cols-2 ${gridCols}`}>
                    {columns.map((column) => {
                        const split = column.links.length > SPLIT_ABOVE;
                        const half = Math.ceil(column.links.length / 2);
                        return (
                            <FoldingColumn key={column.title} title={column.title} className={split ? 'lg:col-span-2' : ''}>
                                {split ? (
                                    <div className="grid grid-cols-2 gap-x-6">
                                        <div><Links links={column.links.slice(0, half)} /></div>
                                        <div><Links links={column.links.slice(half)} /></div>
                                    </div>
                                ) : <Links links={column.links} />}
                            </FoldingColumn>
                        );
                    })}
                    {hasPromise ? (
                        <FoldingColumn title={footer.promiseTitle}>
                            {promises.length ? (
                                <ul className="space-y-2 text-sm text-white/85">
                                    {promises.map((promise) => <li key={promise} className="flex gap-2"><TickIcon className="mt-1 h-3 w-4 shrink-0" /> {promise}</li>)}
                                </ul>
                            ) : null}
                            {footer.trustpilotLink?.url ? (
                                <a href={footer.trustpilotLink.url} target={footer.trustpilotLink.target || '_blank'} rel="noopener" className="mt-4 inline-block hover:opacity-85">
                                    <img src={trustpilotLogo} alt={footer.trustpilotLink.name || 'Trustpilot'} className="h-7 w-auto" loading="lazy" />
                                </a>
                            ) : null}
                        </FoldingColumn>
                    ) : null}
                </div>

                <div className="mt-10 md:mt-12 md:border-t md:border-white/15 md:pt-8">
                    {accreditations.length || awards.length ? (
                        <div className="flex flex-col items-center gap-8 md:flex-row md:justify-between">
                            <div className="flex flex-wrap items-center justify-center gap-6">
                                {accreditations.map((logo, i) => <Logo key={i} logo={logo} imageClass="h-9 w-auto rounded-sm" />)}
                            </div>
                            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-[1.5625rem]">
                                {awards.map((logo, i) => <Logo key={i} logo={logo} imageClass="h-16 w-auto md:h-[7.0625rem]" />)}
                            </div>
                        </div>
                    ) : null}
                    <div className="mt-6 flex flex-col items-center gap-2 md:flex-row md:items-end md:justify-between">
                        {footer.legalText ? <p className="max-w-xl text-center text-xs text-white/70 md:text-left">{footer.legalText}</p> : null}
                        {version ? <span className="shrink-0 text-[0.6875rem] text-white/40 md:ml-auto">v{version}</span> : null}
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default SiteFooter;
